export interface RequestSection {
  title: string;
  fields: string[];
}

export interface RequestTypeConfig {
  label: string;
  sections: RequestSection[];
  labels: Record<string, string>;
}

const config: Record<string, RequestTypeConfig> = {
  residence_certificate: {
    label: 'Atestado de Residência',
    sections: [
      { title: 'Dados Pessoais', fields: ['name', 'birthDate', 'nif', 'phone', 'email'] },
      {
        title: 'Documento',
        fields: [
          'documentType',
          'documentTypeOther',
          'documentNumber',
          'fatherName',
          'motherName',
          'maritalStatus',
        ],
      },
      { title: 'Morada', fields: ['street', 'postalCode', 'locality', 'residenceTime'] },
      { title: 'Pedido', fields: ['purpose', 'copies', 'pickupLocation'] },
    ],
    labels: {
      name: 'Nome',
      birthDate: 'Data de Nascimento',
      nif: 'NIF',
      phone: 'Telefone',
      email: 'Correio eletrónico',
      documentType: 'Tipo de Documento',
      documentTypeOther: 'Outro Documento',
      documentNumber: 'Número do Documento',
      fatherName: 'Nome do Pai',
      motherName: 'Nome da Mãe',
      maritalStatus: 'Estado Civil',
      street: 'Rua',
      postalCode: 'Código Postal',
      locality: 'Localidade',
      residenceTime: 'Tempo de Residência',
      purpose: 'Finalidade',
      copies: 'Número de Cópias',
      pickupLocation: 'Local de Levantamento',
    },
  },
  venue_booking: {
    label: 'Reserva de Espaço',
    sections: [
      { title: 'Requerente', fields: ['name', 'entity', 'email', 'phone'] },
      {
        title: 'Reserva',
        fields: ['space', 'eventPurpose', 'date', 'startTime', 'endTime', 'attendees'],
      },
      { title: 'Equipamento', fields: ['equipment'] },
      { title: 'Observações', fields: ['notes'] },
    ],
    labels: {
      name: 'Nome',
      entity: 'Entidade',
      email: 'Correio eletrónico',
      phone: 'Telefone',
      space: 'Espaço',
      eventPurpose: 'Finalidade do Evento',
      date: 'Data',
      startTime: 'Hora de Início',
      endTime: 'Hora de Fim',
      attendees: 'Número de Participantes',
      equipment: 'Equipamento Necessário',
      notes: 'Observações',
    },
  },
  social_support: {
    label: 'Apoio Social',
    sections: [
      {
        title: 'Dados do Requerente',
        fields: ['name', 'birthDate', 'nif', 'niss', 'phone', 'email', 'address'],
      },
      {
        title: 'Agregado Familiar',
        fields: ['householdSize', 'employmentStatus', 'monthlyIncome', 'householdDescription'],
      },
      { title: 'Apoio Solicitado', fields: ['supportTypes', 'situation'] },
    ],
    labels: {
      name: 'Nome',
      birthDate: 'Data de Nascimento',
      nif: 'NIF',
      niss: 'NISS',
      phone: 'Telefone',
      email: 'Correio eletrónico',
      address: 'Morada',
      householdSize: 'Dimensão do Agregado',
      employmentStatus: 'Situação Laboral',
      monthlyIncome: 'Rendimento Mensal (€)',
      householdDescription: 'Composição do Agregado',
      supportTypes: 'Tipos de Apoio',
      situation: 'Descrição da Situação',
    },
  },
  noise_permit: {
    label: 'Licença especial de ruído',
    sections: [
      {
        title: 'Requerente',
        fields: ['name', 'nif', 'email', 'phone', 'entity', 'requesterAddress'],
      },
      {
        title: 'Atividade',
        fields: ['activityType', 'activityLocation', 'date', 'startTime', 'endTime', 'noiseSource'],
      },
      { title: 'Medidas', fields: ['mitigationMeasures', 'responsibleName', 'responsiblePhone'] },
    ],
    labels: {
      name: 'Nome',
      nif: 'NIF',
      email: 'Correio eletrónico',
      phone: 'Telefone',
      entity: 'Entidade',
      requesterAddress: 'Morada do Requerente',
      activityType: 'Tipo de Atividade',
      activityLocation: 'Local da Atividade',
      date: 'Data',
      startTime: 'Hora de Início',
      endTime: 'Hora de Fim',
      noiseSource: 'Fonte de Ruído',
      mitigationMeasures: 'Medidas de Mitigação',
      responsibleName: 'Responsável',
      responsiblePhone: 'Telefone do Responsável',
    },
  },
  certificate: {
    label: 'Atestado / Declaração',
    sections: [
      { title: 'Tipo de atestado', fields: ['certificateType'] },
      {
        title: 'Dados Pessoais',
        fields: ['name', 'birthDate', 'nif', 'phone', 'email', 'address'],
      },
      { title: 'Pedido', fields: ['purpose', 'copies', 'pickupLocation'] },
    ],
    labels: {
      certificateType: 'Tipo de Certidão',
      name: 'Nome',
      birthDate: 'Data de Nascimento',
      nif: 'NIF',
      phone: 'Telefone',
      email: 'Correio eletrónico',
      address: 'Morada',
      purpose: 'Finalidade',
      copies: 'Número de Cópias',
      pickupLocation: 'Local de Levantamento',
    },
  },
  maintenance: {
    label: 'Comunicação de ocorrência',
    sections: [
      { title: 'Identificação (opcional)', fields: ['name', 'email', 'phone'] },
      { title: 'Ocorrência', fields: ['issueType', 'location', 'description'] },
      { title: 'Urgência', fields: ['urgency', 'isImmediateDanger'] },
    ],
    labels: {
      name: 'Nome',
      email: 'Correio eletrónico',
      phone: 'Telefone',
      issueType: 'Tipo de Problema',
      location: 'Localização',
      description: 'Descrição',
      urgency: 'Urgência',
      isImmediateDanger: 'Perigo Imediato',
    },
  },
};

export function getRequestTypeConfig(type: string): RequestTypeConfig {
  return (
    config[type] ?? {
      label: type,
      sections: [{ title: 'Dados', fields: [] }],
      labels: {},
    }
  );
}

export const ALL_REQUEST_TYPES = Object.keys(config);

export default config;
